import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, FileAudio, FileVideo, FileText, Link2, Loader2, Smartphone, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button, Card } from '../components/UIComponents';
import { SearchableSelect } from '../components/SearchableSelect';
import MediaDropzone from '../components/MediaDropzone';
import { LinkImportPlatformChips } from '../components/LinkImportPlatformChips';
import { useUploadManager } from '../contexts/UploadManagerContext';
import PhoneUploadLinkModal from '../components/PhoneUploadLinkModal';
import { useTranslation } from 'react-i18next';
import {
  createLinkImport,
  getLinkImport,
  previewLinkImport,
  resolveLinkImportThumbnailUrl,
  type LinkImport,
  type LinkImportPreview,
} from '../api/linkImports';
import { clearLinkImportPreview, loadLinkImportPreview, saveLinkImportPreview } from '../utils/linkImportCache';
import {
  getLinkImportProviderLabel,
  isSupportedLinkImportUrl,
} from '../utils/linkImportPlatforms';

function getFileIcon(file: File) {
  if (file.type.startsWith('video/')) return <FileVideo className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />;
  return <FileAudio className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />;
}

function formatBytes(bytes: number) {
  const kb = 1024;
  const mb = kb * 1024;
  const gb = mb * 1024;
  if (bytes >= gb) return `${(bytes / gb).toFixed(2)} GB`;
  if (bytes >= mb) return `${(bytes / mb).toFixed(2)} MB`;
  if (bytes >= kb) return `${(bytes / kb).toFixed(2)} KB`;
  return `${bytes} B`;
}

function fileKey(file: File) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

function fileExt(name: string) {
  const parts = name.split('.');
  if (parts.length < 2) return '';
  return parts[parts.length - 1].toUpperCase();
}

function formatLinkDuration(seconds: number) {
  const totalSeconds = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = totalSeconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueUploads } = useUploadManager();
  const [isPhoneUploadOpen, setIsPhoneUploadOpen] = useState(false);
  const cachedLinkPreview = useMemo(() => loadLinkImportPreview(), []);

  const [entryMode, setEntryMode] = useState<'file' | 'link'>('file');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [linkUrl, setLinkUrl] = useState(cachedLinkPreview?.url ?? '');
  const [linkPreview, setLinkPreview] = useState<LinkImportPreview | null>(cachedLinkPreview?.preview ?? null);
  const [linkImport, setLinkImport] = useState<LinkImport | null>(null);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [isPreviewingLink, setIsPreviewingLink] = useState(false);
  const [isStartingLinkImport, setIsStartingLinkImport] = useState(false);
  const [options, setOptions] = useState({
    language: 'auto',
    speakerDetection: false,
  });

  const addFiles = useCallback((files: File[]) => {
    setSelectedFiles((prev) => {
      const existing = new Set(prev.map(fileKey));
      const merged = [...prev];
      for (const f of files) {
        const k = fileKey(f);
        if (existing.has(k)) continue;
        existing.add(k);
        merged.push(f);
      }
      return merged;
    });
  }, []);

  const removeFile = useCallback((key: string) => {
    setSelectedFiles((prev) => prev.filter((f) => fileKey(f) !== key));
  }, []);

  const startUploads = useCallback(() => {
    if (selectedFiles.length === 0) return;
    enqueueUploads(selectedFiles, options);
    setSelectedFiles([]);
  }, [enqueueUploads, options, selectedFiles]);

  const totalBytes = useMemo(() => selectedFiles.reduce((acc, f) => acc + f.size, 0), [selectedFiles]);

  const canPreviewLink = useMemo(() => isSupportedLinkImportUrl(linkUrl), [linkUrl]);

  const optionsSummary = useMemo(() => {
     const languageLabel = options.language === 'auto' ? t('upload.autoDetectLabel') : options.language.toUpperCase();
     const extras: string[] = [];
     if (options.speakerDetection) extras.push(t('upload.speakerIdShort'));
     return `${languageLabel}${extras.length ? ` · ${extras.join(' · ')}` : ''}`;
  }, [options.language, options.speakerDetection]);

  useEffect(() => {
    if (!linkImport || linkImport.status === 'completed' || linkImport.status === 'failed' || linkImport.status === 'cancelled') {
      return;
    }
    const timer = window.setInterval(async () => {
      try {
        const latest = await getLinkImport(linkImport.id);
        setLinkImport(latest);
        if (latest.transcription_id) {
          window.clearInterval(timer);
          navigate(`/transcription/${latest.transcription_id}`);
        }
      } catch {
        setLinkError('Unable to refresh link import status.');
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [linkImport, navigate]);

  useEffect(() => {
    if (!linkPreview || linkPreview.metadata_status !== 'pending') {
      return;
    }
    const previewUrl = linkPreview.normalized_url || linkUrl;
    let attempts = 0;
    const timer = window.setInterval(async () => {
      attempts += 1;
      try {
        const preview = await previewLinkImport(previewUrl);
        setLinkPreview(preview);
        saveLinkImportPreview(previewUrl, preview);
        if (preview.metadata_status !== 'pending' || attempts >= 15) {
          window.clearInterval(timer);
        }
      } catch {
        if (attempts >= 3) {
          window.clearInterval(timer);
        }
      }
    }, 2000);
    return () => window.clearInterval(timer);
  }, [linkPreview, linkUrl]);

  const previewLink = useCallback(async () => {
    setLinkError(null);
    setLinkPreview(null);
    setLinkImport(null);
    if (!isSupportedLinkImportUrl(linkUrl)) {
      setLinkError('Paste a valid supported media link.');
      return;
    }
    setIsPreviewingLink(true);
    try {
      const preview = await previewLinkImport(linkUrl);
      setLinkPreview(preview);
      saveLinkImportPreview(linkUrl, preview);
    } catch {
      setLinkError('This media link cannot be previewed right now.');
    } finally {
      setIsPreviewingLink(false);
    }
  }, [linkUrl]);

  const clearLinkPreview = useCallback(() => {
    setLinkUrl('');
    setLinkPreview(null);
    setLinkImport(null);
    setLinkError(null);
    clearLinkImportPreview();
  }, []);

  const startLinkImport = useCallback(async () => {
    setLinkError(null);
    if (!linkPreview) return;
    setIsStartingLinkImport(true);
    try {
      const created = await createLinkImport({
        url: linkPreview.normalized_url || linkUrl,
        language: options.language === 'auto' ? undefined : options.language,
        enable_diarization: options.speakerDetection,
      });
      setLinkImport(created);
    } catch {
      setLinkError('Unable to start media import. Check your quota or try another link.');
    } finally {
      setIsStartingLinkImport(false);
    }
  }, [linkPreview, linkUrl, options.language, options.speakerDetection]);

  const isLinkMode = entryMode === 'link';
  const canStartLinkImport = isLinkMode && !!linkPreview && !linkImport && !isStartingLinkImport;
  const readyDescription = isLinkMode
    ? linkImport
      ? `${linkImport.status} · ${linkImport.progress}%`
      : linkPreview
        ? 'Ready to transcribe this media link.'
        : linkUrl
          ? 'Search the link before starting.'
          : 'Paste a media link to start.'
    : selectedFiles.length > 0
      ? t('upload.readyFilesSummary', { count: selectedFiles.length, size: formatBytes(totalBytes) })
      : t('upload.selectAtLeastOneFile');
  const readyButtonLabel = isLinkMode
    ? linkImport
      ? 'Importing...'
      : 'Start transcription'
    : `${t('upload.startUploads')}${selectedFiles.length ? ` (${selectedFiles.length})` : ''}`;
  const readyButtonDisabled = isLinkMode ? !canStartLinkImport : selectedFiles.length === 0;
  const readyButtonTitle = isLinkMode
    ? !linkPreview
      ? 'Search a supported media link first.'
      : undefined
    : selectedFiles.length === 0
      ? t('upload.selectAtLeastOneFileTitle')
      : undefined;
  const handleReadyAction = isLinkMode ? startLinkImport : startUploads;

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-6xl mx-auto space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0">
             <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{t('upload.newTranscription')}</h1>
             <p className="text-sm text-slate-500 dark:text-zinc-400 mt-1">
               {t('upload.subtitle')}
             </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
             <Button variant="secondary" onClick={() => setIsPhoneUploadOpen(true)}>
               <Smartphone className="h-4 w-4" />
               {t('upload.phone')}
             </Button>
             <Button variant="secondary" onClick={() => navigate('/history?folder=all')}>
               <FileText className="h-4 w-4" />
               {t('upload.history')}
             </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5">
          <div className="space-y-4">
            <Card className="overflow-hidden p-0">
              <div className="grid grid-cols-2 border-b border-slate-200 dark:border-zinc-800 px-4 pt-4">
                <button
                  type="button"
                  onClick={() => setEntryMode('file')}
                  className={`relative px-3 pb-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:text-slate-950 dark:focus-visible:text-white ${
                    entryMode === 'file'
                      ? 'text-slate-950 dark:text-white'
                      : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'
                  }`}
                >
                  Upload File
                  {entryMode === 'file' && (
                    <span className="absolute inset-x-8 -bottom-px h-0.5 rounded-full bg-indigo-500" />
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setEntryMode('link')}
                  className={`relative px-3 pb-3 text-sm font-semibold transition-colors focus:outline-none focus-visible:text-slate-950 dark:focus-visible:text-white ${
                    entryMode === 'link'
                      ? 'text-slate-950 dark:text-white'
                      : 'text-slate-500 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300'
                  }`}
                >
                  Paste Link
                  {entryMode === 'link' && (
                    <span className="absolute inset-x-8 -bottom-px h-0.5 rounded-full bg-indigo-500" />
                  )}
                </button>
              </div>

              <div className="p-4 sm:p-5">
                {entryMode === 'file' ? (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                         <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{t('upload.filesTitle')}</p>
                         <p className="text-xs text-slate-500 dark:text-zinc-400">
                           {t('upload.filesHelp')}
                         </p>
                      </div>
                      {selectedFiles.length > 0 && (
                        <Button variant="ghost" size="sm" onClick={() => setSelectedFiles([])}>
                          {t('upload.clear')}
                        </Button>
                      )}
                    </div>

                    <MediaDropzone
                      embedded={selectedFiles.length === 0}
                      multiple={true}
                      title={selectedFiles.length === 0 ? t('upload.dropzoneEmpty') : t('upload.dropzoneAddMore')}
                      onFilesSelected={(files) => addFiles(files)}
                    />

                    {selectedFiles.length > 0 && (
                      <div>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
                             {t('upload.selected', { count: selectedFiles.length })}
                          </p>
                           <p className="text-xs text-slate-500 dark:text-zinc-400">{t('upload.totalSize', { size: formatBytes(totalBytes) })}</p>
                        </div>
                        <div className="space-y-2 max-h-[340px] overflow-y-auto pe-1">
                          {selectedFiles.map((f) => {
                            const key = fileKey(f);
                            const ext = fileExt(f.name);
                            return (
                              <div
                                key={key}
                                className="group flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-3 py-2 hover:bg-slate-50 dark:hover:bg-zinc-800/40 transition-colors"
                              >
                                <div className="flex items-center gap-3 min-w-0">
                                  <div className="h-9 w-9 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-500/20">
                                    {getFileIcon(f)}
                                  </div>
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-slate-900 dark:text-zinc-100 truncate" title={f.name}>
                                      {f.name}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-zinc-400">
                                      {ext && (
                                        <span className="uppercase bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 px-1.5 py-0.5 rounded text-[10px] font-bold text-indigo-600 dark:text-indigo-400 tracking-wide">
                                          {ext}
                                        </span>
                                      )}
                                      <span>{formatBytes(f.size)}</span>
                                    </div>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-white dark:hover:bg-zinc-900 dark:text-zinc-500 dark:hover:text-zinc-300 transition-colors opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
                                  onClick={() => removeFile(key)}
                                  aria-label={`Remove ${f.name}`}
                                   title={t('upload.remove')}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-5 text-center">
                    <div>
                      <p className="text-sm leading-relaxed text-slate-600 dark:text-zinc-400">
                        Paste a media link to preview content before transcription.
                      </p>
                      <div className="mt-4 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 dark:text-zinc-400">
                        <Link2 className="h-3.5 w-3.5" />
                        Supported platforms
                      </div>
                      <LinkImportPlatformChips />
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3">
                      <div className="relative flex-1">
                        <Link2 className="absolute start-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <input
                          value={linkUrl}
                          onChange={(event) => {
                            setLinkUrl(event.target.value);
                            setLinkPreview(null);
                            setLinkImport(null);
                            setLinkError(null);
                          }}
                          placeholder="Paste a YouTube, TikTok, Instagram, Facebook, or X link"
                          className="w-full h-12 rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 ps-11 pe-16 text-sm text-slate-900 dark:text-zinc-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                        {linkUrl && (
                          <button
                            type="button"
                            onClick={clearLinkPreview}
                            className="absolute end-2 top-1/2 -translate-y-1/2 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <Button
                        className="h-12 px-6 text-sm"
                        onClick={previewLink}
                        disabled={!canPreviewLink || isPreviewingLink || isStartingLinkImport}
                      >
                        {isPreviewingLink && <Loader2 className="h-4 w-4 animate-spin" />}
                        Search
                      </Button>
                    </div>

                    {linkError && (
                      <div className="rounded-lg border border-red-200 dark:border-red-500/30 bg-red-50 dark:bg-red-500/10 px-3 py-2 text-sm text-red-700 dark:text-red-300 text-start">
                        {linkError}
                      </div>
                    )}

                    {linkPreview && (
                      <div className="space-y-4 pt-1">
                        <div className="mx-auto max-w-2xl text-center">
                          <p className="text-base sm:text-lg font-semibold leading-snug text-slate-950 dark:text-white line-clamp-3">
                            {linkPreview.title || `${getLinkImportProviderLabel(linkPreview.provider)} media`}
                          </p>
                          <div className="mt-2 flex items-center justify-center gap-2 text-sm font-medium text-slate-500 dark:text-zinc-400">
                            {linkPreview.metadata_status === 'pending' && <Loader2 className="h-4 w-4 animate-spin" />}
                            <span>
                              {linkPreview.duration > 0
                                ? formatLinkDuration(linkPreview.duration)
                                : linkPreview.metadata_status === 'pending'
                                  ? 'Resolving media details...'
                                  : 'Duration will be checked by the worker before import'}
                            </span>
                          </div>
                        </div>

                        {resolveLinkImportThumbnailUrl(linkPreview) && (
                          <img
                            src={resolveLinkImportThumbnailUrl(linkPreview)}
                            alt=""
                            onError={(event) => {
                              if (linkPreview.thumbnail_url && event.currentTarget.src !== linkPreview.thumbnail_url) {
                                event.currentTarget.src = linkPreview.thumbnail_url;
                              }
                            }}
                            className="mx-auto w-full max-w-lg rounded-lg bg-slate-100 dark:bg-zinc-900 object-cover shadow-sm"
                          />
                        )}

                        {linkImport && (
                          <div className="mx-auto max-w-lg">
                            <div className="flex items-center justify-between text-xs text-slate-500 dark:text-zinc-400 mb-1">
                              <span className="capitalize">{linkImport.status}</span>
                              <span>{linkImport.progress}%</span>
                            </div>
                            <div className="h-2 rounded-full bg-slate-200 dark:bg-zinc-800 overflow-hidden">
                              <div className="h-full bg-indigo-600 transition-all" style={{ width: `${linkImport.progress}%` }} />
                            </div>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          <div className="space-y-4 lg:sticky lg:top-6 h-fit">
            <Card className="p-0 overflow-visible">
              <div className="px-4 py-3 border-b border-slate-100 dark:border-zinc-800">
                 <p className="font-semibold text-slate-900 dark:text-zinc-100">{t('upload.optionsTitle')}</p>
                <p className="text-xs text-slate-500 dark:text-zinc-400 mt-0.5">{optionsSummary}</p>
              </div>
              <div className="divide-y divide-slate-100 dark:divide-zinc-800 overflow-visible">
                <div className="p-4 relative z-10">
                  <label className="block text-sm font-semibold text-slate-900 dark:text-zinc-100 mb-2">
                     {t('upload.sourceLanguage')}
                  </label>
                  <SearchableSelect
                    options={[
                      { value: 'auto', label: `✨ ${t('upload.options.autoDetect')} (${t('upload.options.default')})` },
                      { value: 'en', label: 'English' },
                      { value: 'zh', label: 'Chinese (Mandarin)' },
                      { value: 'es', label: 'Spanish' },
                      { value: 'fr', label: 'French' },
                      { value: 'de', label: 'German' },
                      { value: 'ja', label: 'Japanese' },
                    ]}
                    value={options.language}
                    onChange={(val) => setOptions({ ...options, language: val })}
                    placeholder={t('upload.selectLanguage')}
                    searchPlaceholder={t('upload.searchLanguages')}
                  />
                </div>

                <div
                  className="p-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  onClick={() => setOptions({ ...options, speakerDetection: !options.speakerDetection })}
                >
                  <div className="flex items-center gap-3 text-start">
                    <div className="bg-emerald-100 dark:bg-emerald-500/10 p-2 rounded-lg shrink-0">
                      <AlertCircle className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <div>
                       <p className="text-sm font-medium text-slate-900 dark:text-zinc-100">{t('upload.speakerIdentification')}</p>
                       <p className="text-xs text-slate-500 dark:text-zinc-400">{t('upload.speakerIdentificationHelp')}</p>
                    </div>
                  </div>
                  <div
                    className={`w-11 h-6 shrink-0 rounded-full relative transition-colors duration-200 ${
                      options.speakerDetection ? 'bg-indigo-600' : 'bg-slate-200 dark:bg-zinc-700'
                    }`}
                  >
                    <div
                      className={`absolute top-0.5 start-0.5 w-5 h-5 bg-white rounded-full transition-all duration-200 shadow-sm ${
                        options.speakerDetection ? 'ltr:translate-x-5 rtl:-translate-x-5' : 'translate-x-0'
                      }`}
                    ></div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                     <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100">{t('upload.ready')}</p>
                  <p className="text-xs text-slate-500 dark:text-zinc-400">
                    {readyDescription}
                  </p>
                </div>
              </div>
              <div className="mt-3 flex gap-3">
                <Button
                  className="flex-1"
                  onClick={handleReadyAction}
                  disabled={readyButtonDisabled}
                  title={readyButtonTitle}
                >
                  {isStartingLinkImport && <Loader2 className="h-4 w-4 animate-spin" />}
                  {readyButtonLabel}
                </Button>
                 <Button variant="outline" onClick={() => navigate('/dashboard')}>
                   {t('upload.backToDashboard')}
                 </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <PhoneUploadLinkModal
        isOpen={isPhoneUploadOpen}
        onClose={() => setIsPhoneUploadOpen(false)}
      />
    </div>
  );
};

export default UploadPage;
